import React, { useState } from 'react';
import { 
  Modal, 
  Input, 
  Radio, 
  Upload, 
  Button, 
  message 
} from 'antd';
import { 
  PlusOutlined, 
  UploadOutlined,
  FolderOutlined,
  FolderAddOutlined,
  FileExcelOutlined,
  EditOutlined,
  CloseOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { uploadImageToOracle } from 'utils/imageUpload';

const { TextArea } = Input;
const CustomAssessmentSectionCreator = ({ 
  sections = [], 
  onSectionAdd,
  isModalVisible = false,  // ← Add this prop
  onCloseModal  // ← Add this prop
}) => {
  const [sectionName, setSectionName] = useState('');
  const [inputMode, setInputMode] = useState('excel');
  const [excelFile, setExcelFile] = useState(null);

  const [manualQuestions, setManualQuestions] = useState([]);
  const [questionText, setQuestionText] = useState('');
  const [questionImage, setQuestionImage] = useState(null);
  const [options, setOptions] = useState([
    { text: '', image: null },
    { text: '', image: null },
    { text: '', image: null },
    { text: '', image: null }
  ]);
  const [correctOptionIndex, setCorrectOptionIndex] = useState(0);

  const resetState = () => {
    setSectionName('');
    setInputMode('excel');
    setExcelFile(null);
  
    setQuestionText('');
    setQuestionImage(null);
    setOptions([
      { text: '', image: null },
      { text: '', image: null },
      { text: '', image: null },
      { text: '', image: null }
    ]);
    setCorrectOptionIndex(0);
    setManualQuestions([]);
  };

  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    if (onCloseModal) onCloseModal();  // ← Use external handler
    resetState();
  };

  const handleExcelChange = info => {
    const file = info.fileList[0]?.originFileObj || null;
    setExcelFile(file);
  };

  const handleQuestionImageChange = info => {
    const file = info.fileList[0]?.originFileObj || null;
    setQuestionImage(file);
  };

  const handleOptionChange = (index, value) => {
    setOptions(prev => {
      const next = [...prev];
      next[index] = { ...next[index], text: value };
      return next;
    });
  };

  const handleOptionImageChange = (index, info) => {
    const file = info.fileList[0]?.originFileObj || null;
    setOptions(prev => {
      const next = [...prev];
      next[index] = { ...next[index], image: file };
      return next;
    });
  };

  const handleAddOption = () => {
    setOptions(prev => [...prev, { text: '', image: null }]);
  };

  const handleRemoveOption = index => {
    setOptions(prev => prev.filter((_, i) => i !== index));
    setCorrectOptionIndex(prev =>
      prev === index ? 0 : prev > index ? prev - 1 : prev
    );
  };

  const handleAddManualQuestion = async () => {
    const trimmedText = questionText.trim();
    const nonEmptyOptions = options.filter(o => o.text.trim() || o.image);

    if (!trimmedText) {
      message.error('Please provide question text');
      return;
    }

    if (nonEmptyOptions.length < 2) {
      message.error('Please provide at least 2 non-empty options');
      return;
    }

    // Validate that the selected correct option is not empty
    const correctOpt = options[correctOptionIndex];
    if (!correctOpt || (!correctOpt.text.trim() && !correctOpt.image)) {
      message.error('The selected correct answer option cannot be empty');
      return;
    }

    // Check for duplicate option texts
    const optionTexts = nonEmptyOptions.map(o => o.text.trim().toLowerCase());
    const duplicates = optionTexts.filter((text, index) => text && optionTexts.indexOf(text) !== index);
    if (duplicates.length > 0) {
      message.error('Two or more options have the same answer text. Please make each option unique.');
      return;
    }

    // Recalculate correct option index relative to non-empty options
    const correctOptOriginal = options[correctOptionIndex];
    const newCorrectIndex = nonEmptyOptions.indexOf(correctOptOriginal);

    try {
      const loadingKey = 'uploading';
      message.loading({ content: 'Uploading images...', key: loadingKey });

      let questionImageKey = null;
      let questionImageUrl = null;
      if (questionImage instanceof File) {
        console.log('📤 Uploading question image:', questionImage.name);
        const uploadResult = await uploadImageToOracle(questionImage);
        if (uploadResult.success) {
          questionImageKey = uploadResult.objectKey;
          questionImageUrl = uploadResult.parUrl;
          console.log('✅ Question image uploaded:', { questionImageKey, questionImageUrl });
        } else {
          throw new Error('Failed to upload question image: ' + uploadResult.error);
        }
      }

      const processedOptions = await Promise.all(
        nonEmptyOptions.map(async (opt) => {
          let optionImageKey = null;
          let optionImageUrl = null;
          
          if (opt.image instanceof File) {
            console.log('📤 Uploading option image:', opt.image.name);
            const uploadResult = await uploadImageToOracle(opt.image);
            if (uploadResult.success) {
              optionImageKey = uploadResult.objectKey;
              optionImageUrl = uploadResult.parUrl;
              console.log('✅ Option image uploaded:', { optionImageKey, optionImageUrl });
            } else {
              throw new Error('Failed to upload option image: ' + uploadResult.error);
            }
          }
          
          return {
            option_text: opt.text.trim(),
            option_image_key: optionImageKey,
            option_image_url: optionImageUrl
          };
        })
      );

      // Convert 0-based index to 1-based index for backend
      const finalCorrectIndex = (newCorrectIndex ?? 0) + 1;
      
      const payloadQuestion = {
        question_text: trimmedText,
        question_image_key: questionImageKey,
        question_image_url: questionImageUrl,
        options: processedOptions,
        correct_opt: finalCorrectIndex, // ✅ Now sending 1-based index (1, 2, 3, 4)
      };

      console.log('📝 Question payload created:', payloadQuestion);
      console.log('✅ Correct option (0-based UI):', correctOptionIndex);
      console.log('✅ Correct option (1-based API):', finalCorrectIndex);
      console.log('✅ Non-empty options count:', nonEmptyOptions.length);

      setManualQuestions(prev => [...prev, payloadQuestion]);
      message.success({ content: 'Question added successfully!', key: loadingKey });

      // Reset form
      setQuestionText('');
      setQuestionImage(null);
      setOptions([
        { text: '', image: null },
        { text: '', image: null },
        { text: '', image: null },
        { text: '', image: null }
      ]);
      setCorrectOptionIndex(0); // ✅ Reset to 0 (first option by default)
    } catch (error) {
      console.error('❌ Error adding question:', error);
      message.error('Failed to add question: ' + error.message);
    }
  };
  
  const handleCreateSection = async () => {
    if (!sectionName.trim()) return;
  
    try {
      const payload = {
        section_name: sectionName.trim(),
      };
  
      if (inputMode === 'excel' && excelFile instanceof File) {
        console.log('📎 Excel file attached:', excelFile.name);
        payload.excel = excelFile;
      } 
      else if (inputMode === 'manual' && manualQuestions.length > 0) {
        payload.questions = manualQuestions;
      }
  
      console.log('📦 Section payload:', payload);
  
      if (typeof onSectionAdd === 'function') {
        await onSectionAdd(payload);
      }
  
      handleCloseModal();
    } catch (error) {
      console.error('❌ Error creating section:', error);
      message.error('Failed to create section: ' + error.message);
    }
  };

  const isCreateDisabled =
    !sectionName.trim() ||
    (inputMode === 'excel' && !excelFile) ||
    (inputMode === 'manual' && manualQuestions.length === 0);

    return (
      <>
        {/* REMOVE the Add Section button div - it's now in parent */}
        
        {/* Just keep the Modal */}
        <Modal
          title={
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 8,
              fontSize: 16,
              fontWeight: 600 
            }}>
              <FolderAddOutlined style={{ fontSize: 18, color: '#1890ff' }} />
              Add Assessment Section
            </div>
          }
          open={isModalVisible}
          onCancel={handleCloseModal}
          width={720}
          footer={[
            <Button 
              key="cancel" 
              onClick={handleCloseModal}
              icon={<CloseOutlined />}
            >
              Cancel
            </Button>,
            <Button
              key="create"
              type="primary"
              onClick={handleCreateSection}
              disabled={isCreateDisabled}
              icon={<CheckCircleOutlined />}
            >
              Create Section
            </Button>,
          ]}
          destroyOnClose
        >
        <div style={{ padding: '16px 0' }}>
          {/* Section Name */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ 
              display: 'block',
              marginBottom: 8, 
              fontSize: 14, 
              fontWeight: 600,
              color: '#262626'
            }}>
              Section Name
            </label>
            <Input
              placeholder="Enter section name"
              value={sectionName}
              onChange={e => setSectionName(e.target.value)}
              prefix={<FolderOutlined style={{ color: '#bfbfbf' }} />}
            />
          </div>

          {/* Input Mode Toggle */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ 
              display: 'block',
              marginBottom: 8, 
              fontSize: 14, 
              fontWeight: 600,
              color: '#262626'
            }}>
              Question Input Method
            </label>
            <Radio.Group
              value={inputMode}
              onChange={e => setInputMode(e.target.value)}
              buttonStyle="solid"
              style={{ width: '100%' }}
            >
              <Radio.Button 
                value="excel" 
                style={{ 
                  width: '50%', 
                  textAlign: 'center',
                  height: 40
                }}
              >
                <FileExcelOutlined /> Excel Upload
              </Radio.Button>
              <Radio.Button 
                value="manual" 
                style={{ 
                  width: '50%', 
                  textAlign: 'center',
                  height: 40
                }}
              >
                <EditOutlined /> Manual Entry
              </Radio.Button>
            </Radio.Group>
          </div>

          {/* Excel Upload UI */}
          {inputMode === 'excel' && (
            <div 
              style={{ 
                padding: 20,
                background: '#fafafa',
                borderRadius: 8,
                border: '1px solid #e8e8e8'
              }}
            >
              <div style={{ 
                marginBottom: 16, 
                fontSize: 14, 
                fontWeight: 600,
                color: '#262626'
              }}>
                Upload Excel File
              </div>
              <Upload
                beforeUpload={() => false}
                onChange={handleExcelChange}
                maxCount={1}
                accept=".xlsx,.xls"
              >
                <Button 
                  icon={<UploadOutlined />}
                  block
                >
                  Select Excel File
                </Button>
              </Upload>
              {excelFile && (
                <div style={{ 
                  marginTop: 12, 
                  padding: 12,
                  background: '#f6ffed',
                  border: '1px solid #b7eb8f',
                  borderRadius: 6,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span style={{ color: '#389e0d' }}>{excelFile.name}</span>
                </div>
              )}
              <div style={{ 
                marginTop: 12, 
                fontSize: 12, 
                color: '#8c8c8c'
              }}>
                The Excel file will be processed on the backend to extract questions and images.
              </div>
            </div>
          )}

          {/* Manual Entry UI */}
          {inputMode === 'manual' && (
            <div 
              style={{ 
                padding: 20,
                background: '#fafafa',
                borderRadius: 8,
                border: '1px solid #e8e8e8',
                maxHeight: 450,
                overflowY: 'auto'
              }}
            >
              {/* Question Text */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: 8, 
                  fontSize: 14, 
                  fontWeight: 600,
                  color: '#262626'
                }}>
                  Question Text
                </label>
                <TextArea
                  rows={3}
                  placeholder="Enter your question"
                  value={questionText}
                  onChange={e => setQuestionText(e.target.value)}
                />
              </div>

              {/* Question Image */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: 8, 
                  fontSize: 14, 
                  fontWeight: 600,
                  color: '#262626'
                }}>
                  Question Image <span style={{ color: '#8c8c8c', fontWeight: 400 }}>(Optional)</span>
                </label>
                <Upload
                  beforeUpload={() => false}
                  onChange={handleQuestionImageChange}
                  maxCount={1}
                  listType="picture-card"
                  accept="image/*"
                  fileList={questionImage ? [{
                  uid: '-question-img',
                  name: questionImage.name,
                  status: 'done',
                  url: URL.createObjectURL(questionImage)
                }] : []}
                >
                  {!questionImage && (
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8, fontSize: 12 }}>Upload</div>
                    </div>
                  )}
                </Upload>
              </div>

              {/* Options */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: 8, 
                  fontSize: 14, 
                  fontWeight: 600,
                  color: '#262626'
                }}>
                  Answer Options
                </label>
                <Radio.Group
                  value={correctOptionIndex}
                  defaultValue={0} // ✅ Add defaultValue
                  onChange={e => {
                    console.log('✅ Correct option changed to:', e.target.value); // ✅ Add log
                    setCorrectOptionIndex(e.target.value);
                  }}
                  style={{ width: '100%' }}
                >
                  {options.map((opt, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: 12,
                        padding: 12,
                        background: '#fff',
                        border: correctOptionIndex === index 
                          ? '1px solid #1890ff' 
                          : '1px solid #e8e8e8',
                        borderRadius: 6,
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 8,
                        marginBottom: 8 
                      }}>
                        <Radio value={index}>
                          <span style={{ fontWeight: 500, fontSize: 13 }}>
                            Option {String.fromCharCode(65 + index)}
                          </span>
                        </Radio>
                        {options.length > 2 && (
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => handleRemoveOption(index)}
                            style={{ marginLeft: 'auto' }}
                          />
                        )}
                      </div>
                      <Input
                        placeholder={`Enter option ${String.fromCharCode(65 + index)}`}
                        value={opt.text}
                        onChange={e => handleOptionChange(index, e.target.value)}
                        style={{ marginBottom: 8 }}
                      />
                      
                      {/* Option Image */}
                      <div>
                        <div style={{ 
                          fontSize: 12, 
                          color: '#8c8c8c',
                          marginBottom: 6
                        }}>
                          Image <span style={{ fontWeight: 400 }}>(Optional)</span>
                        </div>
                        <Upload
                          beforeUpload={() => false}
                          onChange={info => handleOptionImageChange(index, info)}
                          maxCount={1}
                          listType="picture-card"
                          accept="image/*"
                          fileList={opt.image ? [{
                            uid: `-opt-${index}`,
                            name: opt.image.name,
                            status: 'done',
                            url: URL.createObjectURL(opt.image)
                          }] : []}
                        >
                          {!opt.image && (
                            <div style={{ fontSize: 11 }}>
                              <PlusOutlined />
                              <div style={{ marginTop: 4 }}>Upload</div>
                            </div>
                          )}
                        </Upload>
                      </div>
                    </div>
                  ))}
                </Radio.Group>

                <Button 
                  type="dashed" 
                  onClick={handleAddOption}
                  icon={<PlusOutlined />}
                  block
                  style={{ marginTop: 8 }}
                >
                  Add Option
                </Button>
              </div>

              {/* Add Question Button */}
              <Button 
                type="primary" 
                onClick={handleAddManualQuestion}
                icon={<CheckCircleOutlined />}
                block
              >
                Add Question to Section
              </Button>

              {/* Summary */}
              {manualQuestions.length > 0 && (
                <div style={{ 
                  marginTop: 16, 
                  padding: 12,
                  background: '#f6ffed',
                  border: '1px solid #b7eb8f',
                  borderRadius: 6,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span style={{ color: '#389e0d' }}>
                    {manualQuestions.length} question{manualQuestions.length > 1 ? 's' : ''} added
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default CustomAssessmentSectionCreator;